import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import ArchivedProjects from "@/components/ArchivedProjects";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Building2, Archive, BookMarked, BookOpen, Users, Search, Sparkles, ArrowUpRight,
  Brain, ShieldCheck, Layers, Loader2, Send, Gauge, X, GraduationCap,
} from "lucide-react";

const PHASE_NEXT = [
  { icon: Brain, title: "Continuous Learning", desc: "Auto Pilot's home — every project, delay, and success trains the engine so predictions get smarter." },
  { icon: ShieldCheck, title: "Governance & Approvals", desc: "Approval templates, workflow templates, and PMO maturity trend analysis." },
];

const TYPE_COLORS = {
  project: "bg-emerald-100 text-emerald-700", task: "bg-blue-100 text-blue-700",
  milestone: "bg-violet-100 text-violet-700", risk: "bg-red-100 text-red-600",
  decision: "bg-amber-100 text-amber-700", action: "bg-sky-100 text-sky-700",
  lesson: "bg-pink-100 text-pink-700",
};

export default function PmoPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState({ archived: 0, templates: 0, library: 0, people: 0 });
  const [templates, setTemplates] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [aiQ, setAiQ] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [intel, setIntel] = useState(null);
  const [drill, setDrill] = useState(null);
  const debounce = useRef(null);
  const canResources = ["supervisor", "admin"].includes(user?.role);
  const [showIntro, setShowIntro] = useState(false);

  useEffect(() => {
    if (!canResources && !localStorage.getItem("pmo-intro-dismissed")) setShowIntro(true);
  }, [canResources]);

  const dismissIntro = () => { localStorage.setItem("pmo-intro-dismissed", "1"); setShowIntro(false); };

  useEffect(() => {
    Promise.allSettled([
      api.get("/projects?archived=true"), api.get("/templates"), api.get("/pmo/artifacts"), api.get("/cards"),
    ]).then(([a, t, l, c]) => {
      setStats({
        archived: a.status === "fulfilled" ? a.value.data.length : 0,
        templates: t.status === "fulfilled" ? t.value.data.length : 0,
        library: l.status === "fulfilled" ? l.value.data.length : 0,
        people: c.status === "fulfilled" ? c.value.data.length : 0,
      });
      if (t.status === "fulfilled") setTemplates(t.value.data);
    });
    if (canResources) api.get("/pmo/resource-intelligence").then(({ data }) => setIntel(data)).catch(() => setIntel([]));
  }, [canResources]);

  useEffect(() => {
    clearTimeout(debounce.current);
    if (!q.trim()) { setResults(null); setSearching(false); return; }
    setSearching(true);
    debounce.current = setTimeout(() => {
      api.get(`/pmo/knowledge-search?q=${encodeURIComponent(q.trim())}`)
        .then(({ data }) => setResults(data))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 400);
    return () => clearTimeout(debounce.current);
  }, [q]);

  const askAi = async (e) => {
    e.preventDefault();
    if (!aiQ.trim() || aiBusy) return;
    setAiBusy(true);
    setAiResult(null);
    try {
      const { data } = await api.post("/pmo/ai-search", { query: aiQ.trim() });
      setAiResult(data);
    } catch { setAiResult({ answer: "Something went wrong — try again.", matches: [] }); }
    finally { setAiBusy(false); }
  };

  const tiles = [
    { icon: Archive, label: "Archived projects", value: stats.archived, sub: "searchable knowledge", color: "#059669", to: "/pmo/archived" },
    { icon: BookMarked, label: "Org templates", value: stats.templates, sub: "reusable plans", color: "#2563EB", to: "/library?tab=templates" },
    { icon: BookOpen, label: "Artifacts on file", value: stats.library, sub: "documents across projects", color: "#7C3AED", to: "/pmo/artifacts" },
    ...(canResources ? [{ icon: Users, label: "Resources tracked", value: stats.people, sub: "across all projects", color: "#D97706", to: "/pmo/registry" }] : []),
  ];

  return (
    <Layout>
      <div className="space-y-10" data-testid="pmo-page">
        <div>
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
            <Building2 className="h-3.5 w-3.5" /> PMO layer
          </span>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-3">Dashboard</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">Your organizational intelligence at a glance — every number, row and card is clickable to drill into the underlying data.</p>
        </div>

        {showIntro && (
          <section className="relative rounded-2xl border border-emerald-300 bg-gradient-to-br from-emerald-50 via-white to-emerald-50/40 p-5" data-testid="pmo-pm-intro">
            <button onClick={dismissIntro} className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors" title="Dismiss" data-testid="pmo-pm-intro-dismiss">
              <X className="h-4 w-4" />
            </button>
            <h2 className="font-display text-lg font-extrabold tracking-tight inline-flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-emerald-600" /> Welcome to the PMO layer — this is what your organization knows
            </h2>
            <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
              Every archived project feeds this knowledge base: its plans, risks, decisions, artifacts and lessons stay searchable forever.
              Before you plan something new, check whether the organization has done it before — and learn from how it went.
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              <Button size="sm" variant="outline" className="gap-1.5 bg-white" onClick={() => nav("/pmo/archived")} data-testid="pmo-intro-archived">
                <Archive className="h-3.5 w-3.5 text-emerald-600" /> Browse archived projects
              </Button>
              <Button size="sm" variant="outline" className="gap-1.5 bg-white" onClick={() => nav("/lessons")} data-testid="pmo-intro-lessons">
                <BookOpen className="h-3.5 w-3.5 text-emerald-600" /> Lessons learned
              </Button>
              <Button size="sm" variant="outline" className="gap-1.5 bg-white" onClick={() => document.querySelector('[data-testid="pmo-ai-input"]')?.focus()} data-testid="pmo-intro-ask">
                <Sparkles className="h-3.5 w-3.5 text-emerald-600" /> Ask the PMO anything
              </Button>
            </div>
          </section>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="pmo-stat-tiles">
          {tiles.map((t) => (
            <button key={t.label} onClick={() => nav(t.to)}
              className="text-left rounded-2xl border border-border bg-white p-4 shadow-sm hover:border-emerald-400/60 hover:shadow-md transition-all group"
              data-testid={`pmo-tile-${t.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="flex items-start justify-between">
                <span className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: `${t.color}1f` }}>
                  <t.icon className="h-4 w-4" style={{ color: t.color }} />
                </span>
                <ArrowUpRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <p className="font-display text-3xl font-extrabold tracking-tight mt-2">{t.value}</p>
              <p className="text-sm font-semibold">{t.label}</p>
              <p className="text-xs text-muted-foreground">{t.sub}</p>
            </button>
          ))}
        </div>

        <section className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-5" data-testid="pmo-ai-search">
          <h2 className="font-display text-lg font-extrabold tracking-tight inline-flex items-center gap-2"><Sparkles className="h-4 w-4 text-emerald-600" /> Ask the PMO</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Ask in plain language — "have we done a telecom implementation?", "find projects with vendor risks".</p>
          <form onSubmit={askAi} className="flex gap-2 mt-3">
            <Input value={aiQ} onChange={(e) => setAiQ(e.target.value)} placeholder="Ask about your organization's project history…" className="bg-white" data-testid="pmo-ai-input" />
            <Button type="submit" disabled={aiBusy || !aiQ.trim()} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid="pmo-ai-ask">
              {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Ask
            </Button>
          </form>
          {aiResult && (
            <div className="mt-4 rounded-xl border border-border bg-white p-4" data-testid="pmo-ai-answer">
              <p className="text-sm leading-relaxed">{aiResult.answer}</p>
              {aiResult.matches?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {aiResult.matches.map((m) => (
                    <button key={m.id} onClick={() => nav(`/projects/${m.id}`)}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full border border-emerald-300 bg-emerald-50 text-emerald-800 px-3 py-1 hover:bg-emerald-100 transition-colors"
                      data-testid={`pmo-ai-match-${m.id}`}>
                      <Archive className="h-3 w-3" /> {m.name}{m.industry ? ` · ${m.industry}` : ""} <ArrowUpRight className="h-3 w-3" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>

        <section data-testid="pmo-knowledge">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="font-display text-lg font-extrabold tracking-tight inline-flex items-center gap-2"><Archive className="h-4 w-4 text-emerald-600" /> Organizational Knowledge</h2>
              <p className="text-sm text-muted-foreground">Search across every archived project's tasks, milestones, risks, decisions, actions and lessons.</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative w-full sm:w-72">
                <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search knowledge…" className="pl-9" data-testid="pmo-search-input" />
              </div>
              <Button variant="outline" size="sm" className="gap-1 shrink-0" onClick={() => nav("/pmo/archived")} data-testid="pmo-open-archive">
                Browse archive <ArrowUpRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
          {searching && <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>}
          {!searching && results !== null && (
            <div className="space-y-3" data-testid="pmo-search-results">
              {results.length === 0 && <p className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-6 text-center">No knowledge matches "{q}".</p>}
              {results.map((r) => (
                <div key={r.project.id} className="rounded-xl border border-border bg-white p-4" data-testid={`pmo-result-${r.project.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-display font-bold truncate">{r.project.name}{r.project.industry ? <span className="text-muted-foreground font-normal text-sm"> · {r.project.industry}</span> : null}</p>
                    <Button size="sm" variant="ghost" className="gap-1 shrink-0" onClick={() => nav(`/projects/${r.project.id}`)} data-testid={`pmo-result-open-${r.project.id}`}>
                      View <ArrowUpRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <div className="mt-2 space-y-1.5">
                    {r.matches.map((m, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm">
                        <span className={`shrink-0 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full mt-0.5 ${TYPE_COLORS[m.type] || "bg-secondary text-muted-foreground"}`}>{m.type}</span>
                        <span className="font-medium">{m.title}</span>
                        {m.snippet && <span className="text-muted-foreground text-xs mt-0.5 truncate">— {m.snippet}</span>}
                      </div>
                    ))}
                    {r.match_count > r.matches.length && <p className="text-[11px] text-muted-foreground">+{r.match_count - r.matches.length} more matches</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
          {!searching && results === null && <ArchivedProjects />}
        </section>

        {canResources && (
        <section data-testid="pmo-resource-intel">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="font-display text-lg font-extrabold tracking-tight inline-flex items-center gap-2 mb-1"><Gauge className="h-4 w-4 text-amber-600" /> Resource Intelligence</h2>
              <p className="text-sm text-muted-foreground">What every project has taught the system about your people — click a row for their actual tasks.</p>
            </div>
            <Button variant="outline" size="sm" className="gap-1 shrink-0" onClick={() => nav("/pmo/registry")} data-testid="pmo-open-registry">
              Open registry <ArrowUpRight className="h-3.5 w-3.5" />
            </Button>
          </div>
          {intel === null && <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>}
          {intel && intel.length === 0 && <p className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-6 text-center">No assigned tasks yet — assign resources to tasks and intelligence builds automatically.</p>}
          {intel && intel.length > 0 && (
            <div className="rounded-xl border border-border bg-white overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-2.5 font-bold">Resource</th>
                    <th className="px-3 py-2.5 font-bold text-right">Projects</th>
                    <th className="px-3 py-2.5 font-bold text-right">Assigned</th>
                    <th className="px-3 py-2.5 font-bold text-right">Done</th>
                    <th className="px-3 py-2.5 font-bold text-right">Open</th>
                    <th className="px-3 py-2.5 font-bold text-right">Overdue</th>
                    <th className="px-4 py-2.5 font-bold w-44">Completion</th>
                  </tr>
                </thead>
                <tbody>
                  {intel.slice(0, 12).map((r) => (
                    <tr key={r.name} onClick={() => setDrill(r.name)}
                      className="border-b border-border/60 last:border-0 cursor-pointer hover:bg-secondary/40 transition-colors"
                      data-testid={`intel-row-${r.name.toLowerCase().replace(/\s+/g, "-")}`}>
                      <td className="px-4 py-2 font-semibold">{r.name}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.projects}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.total}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{r.done}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.open}</td>
                      <td className={`px-3 py-2 text-right tabular-nums font-bold ${r.overdue ? "text-red-600" : "text-muted-foreground"}`}>{r.overdue}</td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 rounded-full bg-secondary overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${r.completion}%`, background: r.completion >= 70 ? "#10B981" : r.completion >= 40 ? "#EAB308" : "#94A3B8" }} />
                          </div>
                          <span className="text-xs font-bold tabular-nums w-9 text-right">{r.completion}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {intel.length > 12 && <p className="text-[11px] text-muted-foreground px-4 py-2">Showing top 12 of {intel.length} resources (sorted by risk).</p>}
            </div>
          )}
        </section>
        )}

        <section data-testid="pmo-templates">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="font-display text-lg font-extrabold tracking-tight inline-flex items-center gap-2"><BookMarked className="h-4 w-4 text-blue-600" /> Organizational Templates</h2>
              <p className="text-sm text-muted-foreground">Program-level plans anyone can start a project from.</p>
            </div>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => nav("/library")} data-testid="pmo-open-library">
              Manage in Library <ArrowUpRight className="h-3.5 w-3.5" />
            </Button>
          </div>
          {templates.length === 0 ? (
            <p className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-6 text-center">No templates yet — save any project as a template from its header.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {templates.slice(0, 6).map((t) => (
                <button key={t.id} onClick={() => nav("/library?tab=templates")}
                  className="text-left rounded-lg border border-border bg-white p-4 hover:border-blue-400/60 hover:shadow-sm transition-all group"
                  data-testid={`pmo-template-${t.id}`}>
                  <div className="flex items-center gap-2">
                    <Layers className="h-4 w-4 text-blue-600 shrink-0" />
                    <p className="font-semibold truncate flex-1">{t.name}</p>
                    <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{(t.tasks || []).length} tasks{t.industry ? ` · ${t.industry}` : ""}</p>
                </button>
              ))}
            </div>
          )}
        </section>

        <section data-testid="pmo-phase2">
          <h2 className="font-display text-lg font-extrabold tracking-tight inline-flex items-center gap-2 mb-4"><Sparkles className="h-4 w-4 text-amber-500" /> Coming next</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {PHASE_NEXT.map((f) => (
              <div key={f.title} className="rounded-lg border border-dashed border-border bg-secondary/30 p-4">
                <f.icon className="h-5 w-5 text-muted-foreground mb-2" />
                <p className="font-semibold text-sm">{f.title} <span className="text-[10px] font-bold uppercase text-amber-600 ml-1">Soon</span></p>
                <p className="text-xs text-muted-foreground mt-1">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <ResourceDrillDialog name={drill} onOpenChange={(v) => !v && setDrill(null)} nav={nav} />
      </div>
    </Layout>
  );
}

function ResourceDrillDialog({ name, onOpenChange, nav }) {
  const [tasks, setTasks] = useState(null);
  useEffect(() => {
    if (!name) return;
    setTasks(null);
    api.get(`/pmo/resource-tasks?name=${encodeURIComponent(name)}`).then(({ data }) => setTasks(data)).catch(() => setTasks([]));
  }, [name]);
  return (
    <Dialog open={!!name} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid="pmo-resource-drill">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Users className="h-5 w-5 text-amber-600" /> {name}</DialogTitle>
          <DialogDescription>Every assigned task across your projects — overdue first. Click one to open its plan.</DialogDescription>
        </DialogHeader>
        {tasks === null && <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>}
        {tasks !== null && tasks.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No assigned tasks found for {name}.</p>}
        {tasks !== null && tasks.length > 0 && (
          <div className="space-y-1.5">
            {tasks.map((t) => (
              <button key={t.id} onClick={() => { onOpenChange(false); nav(`/projects/${t.project_id}?tab=plan`); }}
                className="w-full text-left rounded-md border border-border px-3 py-2 hover:border-primary/40 hover:bg-secondary/30 transition-colors"
                data-testid={`drill-task-${t.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold truncate">{t.title}</p>
                  <span className="text-xs font-bold tabular-nums shrink-0">{t.status === "done" ? 100 : t.percent_complete}%</span>
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
                  <span className="truncate">{t.project_name}{t.project_archived ? " (archived)" : ""}</span>
                  {t.end_date && <span className={t.overdue ? "text-red-600 font-bold" : ""}>· due {t.end_date}{t.overdue ? " ⚠ overdue" : ""}</span>}
                  {t.track_status === "at_risk" && <span className="text-amber-600 font-bold">· at risk</span>}
                  {t.track_status === "jeopardy" && <span className="text-red-600 font-bold">· jeopardy</span>}
                  {t.status === "done" && <span className="text-emerald-600 font-bold">· done</span>}
                </p>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
